import { useCallback, useEffect, useRef, useState } from "react";
import { Btn, P } from "../../../../../AbstractElements";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../ReduxToolkit/Hooks";
import { closeModal } from "../../../../../ReduxToolkit/Slice/UserProfileDetailsModalSlice";
import {
  Cancel,
  Preview,
  profile,
  profileImageAlt,
  Upload,
} from "../../../../../utils/Constant";
import CommonModal from "../../../../../Component/Ui-Kits/Modal/Common/CommonModal";
import ReactCrop, {
  convertToPixelCrop,
  Crop,
  PixelCrop,
} from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { canvasToFile } from "../../../../../utils/fileUtils";
import { useModalSelector } from "../../../../../hooks/useModalSelector";
import { useUpdateEmployeeProfilePicMutation } from "../../../../../ReduxToolkit/api/employee";
import { showApiResultAlert } from "../../../../../utils/AlertManager/showApiAlert";
import { alertManager } from "../../../../../utils/AlertManager";
import IconComponent from "../../../../../CommonElements/IconComponent";
import { setIsOpenAlertPicModal } from "../../../redux/slice/EmployeeManagementSlice";
import styled, { createGlobalStyle } from "styled-components";
import {
  MAX_PROFILE_IMAGE_SIZE,
  PROFILE_IMAGE_ACCEPT,
  PROFILE_IMAGE_CROP_ASPECT,
  PROFILE_IMAGE_SIZE_VALIDATION_MESSAGE,
} from "./profileImageCrop.constants";
import {
  centerAspectCrop,
  getSelectedImageFile,
  isObjectUrl,
  renderCropToCanvas,
  resolveProfileImageUserId,
  validateProfileImageFile,
} from "./profileImageCrop.utils";

type ProfileImageCropModalProps = {
  selectedProfileImage: string;
  addedUserId: number;
  setSelectedProfileImage: any;
  employeeProfileDetails: any;
  loggedInEmployee: any;
};

type ImageDisplaySize = {
  height: number;
  width: number;
};

const getStageContentSize = (element: HTMLElement) => {
  const styles = window.getComputedStyle(element);
  const horizontalPadding =
    Number.parseFloat(styles.paddingLeft) +
    Number.parseFloat(styles.paddingRight);
  const verticalPadding =
    Number.parseFloat(styles.paddingTop) +
    Number.parseFloat(styles.paddingBottom);

  return {
    height: Math.max(0, element.clientHeight - verticalPadding),
    width: Math.max(0, element.clientWidth - horizontalPadding),
  };
};

const containImageInsideStage = (
  naturalWidth: number,
  naturalHeight: number,
  stageWidth: number,
  stageHeight: number,
): ImageDisplaySize | null => {
  if (
    naturalWidth <= 0 ||
    naturalHeight <= 0 ||
    stageWidth <= 0 ||
    stageHeight <= 0
  ) {
    return null;
  }

  // Never enlarge a small source image. Large images are reduced until both
  // dimensions fit completely inside the visible crop stage.
  const scale = Math.min(
    stageWidth / naturalWidth,
    stageHeight / naturalHeight,
    1,
  );

  return {
    height: Math.max(1, Math.floor(naturalHeight * scale)),
    width: Math.max(1, Math.floor(naturalWidth * scale)),
  };
};

const ProfileImageModalGlobalStyle = createGlobalStyle`
  .profile-image-crop-modal {
    width: min(1080px, calc(100vw - 32px));
    max-width: min(1080px, calc(100vw - 32px));
    height: min(820px, calc(100dvh - 32px));
    max-height: calc(100dvh - 32px);
    margin: 16px auto;
  }

  .profile-image-crop-modal .profile-image-crop-content {
    display: flex;
    width: 100%;
    height: 100%;
    max-height: 100%;
    overflow: hidden;
    border-radius: 16px;
  }

  .profile-image-crop-modal .modal-body {
    display: flex;
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
  }

  @supports not (height: 1dvh) {
    .profile-image-crop-modal {
      height: min(820px, calc(100vh - 32px));
      max-height: calc(100vh - 32px);
    }
  }

  @media (min-width: 700px) and (max-width: 991px) {
    .profile-image-crop-modal {
      width: calc(100vw - 24px);
      max-width: calc(100vw - 24px);
      height: min(760px, calc(100dvh - 24px));
      max-height: calc(100dvh - 24px);
      margin: 12px auto;
    }

    @supports not (height: 1dvh) {
      .profile-image-crop-modal {
        height: min(760px, calc(100vh - 24px));
        max-height: calc(100vh - 24px);
      }
    }
  }

  @media (max-width: 699px) {
    .profile-image-crop-modal {
      width: calc(100vw - 12px);
      max-width: calc(100vw - 12px);
      height: calc(100dvh - 12px);
      max-height: calc(100dvh - 12px);
      margin: 6px auto;
    }

    .profile-image-crop-modal .profile-image-crop-content {
      border-radius: 14px;
    }

    @supports not (height: 1dvh) {
      .profile-image-crop-modal {
        height: calc(100vh - 12px);
        max-height: calc(100vh - 12px);
      }
    }
  }

  @media (max-width: 699px) and (max-height: 520px) {
    .profile-image-crop-modal {
      width: 100%;
      max-width: 100%;
      height: 100dvh;
      max-height: 100dvh;
      margin: 0;
    }

    .profile-image-crop-modal .profile-image-crop-content {
      border-radius: 0;
    }

    @supports not (height: 1dvh) {
      .profile-image-crop-modal {
        height: 100vh;
        max-height: 100vh;
      }
    }
  }
`;

const ModalShell = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  min-height: 0;
  max-height: 100%;
  flex-direction: column;
  overflow: hidden;
  background: #f6f8fb;
  -webkit-overflow-scrolling: touch;
`;

const ModalHeader = styled.div`
  position: relative;
  flex: 0 0 auto;
  padding: 18px 22px 15px;
  border-bottom: 1px solid #e2e8f0;
  background: #ffffff;

  &::before {
    position: absolute;
    inset: 0 0 auto 0;
    height: 3px;
    background: linear-gradient(90deg, #2563eb, #14b8a6);
    content: "";
  }

  @media (max-width: 699px) {
    padding: 13px 12px 11px;
  }

  @media (max-width: 380px) {
    padding: 11px 10px 9px;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  max-width: 760px;
`;

const HeaderIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  border: 1px solid #bfdbfe;
  border-radius: 12px;
  color: #1d4ed8;
  background: #eff6ff;
  box-shadow: 0 8px 18px rgba(37, 99, 235, 0.14);

  @media (max-width: 699px) {
    width: 36px;
    height: 36px;
    flex-basis: 36px;
    border-radius: 10px;
  }

  @media (max-width: 380px) {
    width: 32px;
    height: 32px;
    flex-basis: 32px;
  }
`;

const HeaderText = styled.div`
  min-width: 0;
  flex: 1 1 auto;
`;

const ModalTitle = styled.h4`
  margin: 0;
  color: #0f172a;
  font-size: 18px;
  font-weight: 700;
  text-align: left;

  @media (max-width: 699px) {
    font-size: 15px;
  }

  @media (max-width: 380px) {
    font-size: 14px;
  }
`;

const ModalDescription = styled(P)`
  max-width: 560px;
  margin: 5px 0 0 !important;
  color: #64748b;
  font-size: 12px;
  line-height: 1.45;
  text-align: left;

  @media (max-width: 699px) {
    margin-top: 3px !important;
    font-size: 10.5px;
    line-height: 1.35;
  }
`;

const ImageReviewPanel = styled.div`
  flex: 1 1 auto;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 240px;
  grid-template-rows: minmax(0, 1fr);
  gap: 14px;
  min-height: 0;
  overflow: hidden;
  padding: 16px;

  @media (min-width: 700px) and (max-width: 991px) {
    grid-template-columns: minmax(0, 1fr) 200px;
    grid-template-rows: minmax(0, 1fr);
    gap: 12px;
    padding: 12px;
  }

  @media (max-width: 699px) {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto auto;
    align-content: start;
    gap: 8px;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: contain;
    scrollbar-gutter: stable;
    padding: 8px;
  }

  @media (max-width: 380px) {
    padding: 6px;
  }
`;

const ImageCard = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  min-height: 0;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.06);

  @media (max-width: 699px) {
    height: auto;
    flex: 0 0 auto;
    border-radius: 12px;
    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.055);
  }
`;

const ImageCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  border-bottom: 1px solid #edf2f7;

  @media (max-width: 699px) {
    min-height: 38px;
    padding: 8px 10px;
  }
`;

const ImageCardTitle = styled.p`
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  gap: 7px;
  margin: 0;
  color: #0f172a;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;

  i {
    color: #2563eb;
  }
`;

const ImageCardHint = styled.span`
  min-width: 0;
  color: #64748b;
  font-size: 10px;
  line-height: 1.25;
  text-align: right;
`;

const ImageStage = styled.div`
  flex: 1 1 0;
  display: flex;
  position: relative;
  width: 100%;
  min-width: 0;
  min-height: 0;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-sizing: border-box;
  padding: 14px;
  background:
    linear-gradient(45deg, #f1f5f9 25%, transparent 25%),
    linear-gradient(-45deg, #f1f5f9 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #f1f5f9 75%),
    linear-gradient(-45deg, transparent 75%, #f1f5f9 75%);
  background-color: #ffffff;
  background-position: 0 0, 0 10px, 10px -10px, -10px 0;
  background-size: 20px 20px;

  .ReactCrop {
    display: inline-flex;
    width: fit-content;
    height: fit-content;
    max-width: none;
    max-height: none;
    flex: 0 0 auto;
    line-height: 0;
    touch-action: none;
    user-select: none;
    border-radius: 10px;
    box-shadow: 0 18px 38px rgba(15, 23, 42, 0.14);
  }

  .ReactCrop__child-wrapper {
    max-width: none;
    max-height: none;
  }

  .ReactCrop__child-wrapper > img {
    max-width: none;
    max-height: none;
  }

  .ReactCrop__crop-selection {
    touch-action: none;
    border: 2px solid #2563eb;
    box-shadow: 0 0 0 9999px rgba(15, 23, 42, 0.46);
  }

  @media (max-width: 699px) {
    flex: 0 0 auto;
    height: clamp(200px, 38dvh, 320px);
    padding: 10px;
  }

  @media (max-width: 575px) {
    height: clamp(190px, 36dvh, 260px);
    padding: 8px;
  }

  @media (max-width: 699px) and (max-height: 560px) {
    height: 190px;
  }

  @supports not (height: 1dvh) {
    @media (max-width: 699px) {
      height: clamp(200px, 38vh, 320px);
    }

    @media (max-width: 575px) {
      height: clamp(190px, 36vh, 260px);
    }
  }
`;

const SelectedImage = styled.img`
  display: block;
  max-width: none;
  max-height: none;
  flex: 0 0 auto;
  border-radius: 10px;
  object-fit: contain;
`;

const PreviewCard = styled(ImageCard)`
  display: flex;
  flex-direction: column;

  @media (max-width: 699px) {
    height: auto;
  }
`;

const PreviewStage = styled.div`
  flex: 1 1 auto;
  display: grid;
  min-height: 0;
  place-items: center;
  padding: 16px;
  background: #ffffff;

  @media (min-width: 700px) and (max-width: 991px) {
    padding: 12px;
  }

  @media (max-width: 699px) {
    flex: 0 0 auto;
    min-height: 108px;
    padding: 10px;
  }

  @media (max-width: 575px) {
    min-height: 96px;
    padding: 8px;
  }
`;

const PreviewCanvas = styled.canvas`
  width: 180px;
  height: 180px;
  max-width: 100%;
  border: 1px solid #dbe3ef;
  border-radius: 999px;
  background: #ffffff;
  box-shadow:
    0 0 0 8px #ffffff,
    0 0 0 9px #dbeafe,
    0 18px 36px rgba(15, 23, 42, 0.14);

  @media (min-width: 700px) and (max-width: 991px) {
    width: 144px;
    height: 144px;
  }

  @media (max-width: 699px) {
    width: 92px;
    height: 92px;
    box-shadow:
      0 0 0 5px #ffffff,
      0 0 0 6px #dbeafe,
      0 10px 22px rgba(15, 23, 42, 0.12);
  }

  @media (max-width: 575px) {
    width: 82px;
    height: 82px;
  }
`;

const PreviewPlaceholder = styled.p`
  max-width: 170px;
  margin: 0;
  color: #94a3b8;
  font-size: 12px;
  text-align: center;
  line-height: 1.45;
`;

const Actions = styled.div`
  flex: 0 0 auto;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 16px 16px;
  border-top: 1px solid #e2e8f0;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(8px);

  @media (max-width: 699px) {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(108px, 1fr));
    gap: 8px;
    padding: 8px 10px calc(8px + env(safe-area-inset-bottom));

    button {
      width: 100%;
      min-width: 0;
      min-height: 38px;
      margin-left: 0 !important;
      padding-right: 10px;
      padding-left: 10px;
      white-space: nowrap;
    }
  }

  @media (max-width: 360px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const ValidationMessage = styled.p`
  flex: 0 0 auto;
  margin: 0 16px 12px;
  color: #dc2626;
  font-size: 12px;
  font-weight: 600;

  @media (max-width: 699px) {
    margin: 0 10px 8px;
    font-size: 11px;
  }
`;

const ProfileImageCropModal = ({
  addedUserId,
  selectedProfileImage,
  setSelectedProfileImage,
  employeeProfileDetails,
  loggedInEmployee,
}: ProfileImageCropModalProps) => {
  const dispatch = useAppDispatch();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const imageStageRef = useRef<HTMLDivElement | null>(null);
  const objectUrlRef = useRef<string | null>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const cropRef = useRef<Crop>();
  const layoutFrameRef = useRef<number | null>(null);
  const shouldResetCropRef = useRef(true);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [displayImageSize, setDisplayImageSize] =
    useState<ImageDisplaySize | null>(null);
  const [validationError, setValidationError] = useState("");
  const [isReadingFile, setIsReadingFile] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(Boolean(selectedProfileImage));
  const [isGeneratingCroppedImage, setIsGeneratingCroppedImage] =
    useState(false);
  const [updateEmployeeProfilePic, { isLoading, reset }] =
    useUpdateEmployeeProfilePicMutation();
  const { isOpen, selectedId } = useModalSelector();
  const { isOpenAlertPicModal } = useAppSelector(
    (state) => state.employeeManagement,
  );

  const employeeJobInfo = employeeProfileDetails?.jobInformation;
  const isMandatoryProfileUpload = Boolean(isOpenAlertPicModal);

  const profilePicAlertMeta = {
    title: `${employeeProfileDetails?.jobInformation?.name} Info`,
    source: "profileImageCropModal",
  };

  const showValidationAlert = (message: string) => {
    alertManager.showAlert({
      type: "warning",
      title: profilePicAlertMeta.title,
      message,
      source: profilePicAlertMeta.source,
    });
  };
  const updateContainedImageLayout = useCallback(() => {
    const stage = imageStageRef.current;
    const image = imageRef.current;

    if (!stage || !image) {
      return;
    }

    const { naturalWidth, naturalHeight } = image;

    if (naturalWidth <= 0 || naturalHeight <= 0) {
      return;
    }

    const stageSize = getStageContentSize(stage);
    const nextDisplaySize = containImageInsideStage(
      naturalWidth,
      naturalHeight,
      stageSize.width,
      stageSize.height,
    );

    if (!nextDisplaySize) {
      return;
    }

    setDisplayImageSize((current) =>
      current?.width === nextDisplaySize.width &&
      current?.height === nextDisplaySize.height
        ? current
        : nextDisplaySize,
    );

    if (layoutFrameRef.current !== null) {
      window.cancelAnimationFrame(layoutFrameRef.current);
    }

    layoutFrameRef.current = window.requestAnimationFrame(() => {
      layoutFrameRef.current = null;

      const shouldResetCrop =
        shouldResetCropRef.current || !cropRef.current;
      const nextCrop = shouldResetCrop
        ? centerAspectCrop(
            nextDisplaySize.width,
            nextDisplaySize.height,
            PROFILE_IMAGE_CROP_ASPECT,
          )
        : cropRef.current;

      if (!nextCrop) {
        return;
      }

      const nextPixelCrop = convertToPixelCrop(
        nextCrop,
        nextDisplaySize.width,
        nextDisplaySize.height,
      );

      cropRef.current = nextCrop;
      setCrop(nextCrop);
      setCompletedCrop((current) =>
        current &&
        current.x === nextPixelCrop.x &&
        current.y === nextPixelCrop.y &&
        current.width === nextPixelCrop.width &&
        current.height === nextPixelCrop.height
          ? current
          : nextPixelCrop,
      );

      shouldResetCropRef.current = false;
      setIsImageLoading(false);
    });
  }, []);
  const isProcessing =
    isReadingFile || isImageLoading || isGeneratingCroppedImage;
  const isUploadDisabled = !completedCrop || isProcessing || isLoading;

  const handleToggle = () => {
    if (isMandatoryProfileUpload || isLoading) return;
    dispatch(setSelectedProfileImage(null));
    dispatch(closeModal());
  };

  const handleOpenFilePicker = () => fileInputRef.current?.click();

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsReadingFile(true);
    const validationMessage = validateProfileImageFile(file);
    if (validationMessage) {
      setValidationError(validationMessage);
      showValidationAlert(validationMessage);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setIsReadingFile(false);
      return;
    }

    const imageUrl = URL.createObjectURL(file);
    setValidationError("");
    cropRef.current = undefined;
    shouldResetCropRef.current = true;
    setCrop(undefined);
    setCompletedCrop(undefined);
    setDisplayImageSize(null);
    setIsImageLoading(true);
    dispatch(setSelectedProfileImage(imageUrl));

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setIsReadingFile(false);
  };

  const handleImageLoad = () => {
    shouldResetCropRef.current = true;
    setIsImageLoading(true);
    window.requestAnimationFrame(updateContainedImageLayout);
  };

  const handleImageError = () => {
    setIsImageLoading(false);
    setValidationError("Unable to load the selected image.");
  };

  useEffect(() => {
    cropRef.current = undefined;
    shouldResetCropRef.current = true;
    setCrop(undefined);
    setCompletedCrop(undefined);
    setDisplayImageSize(null);
    setIsImageLoading(Boolean(selectedProfileImage));

    if (!selectedProfileImage) {
      return;
    }

    const frameId = window.requestAnimationFrame(updateContainedImageLayout);

    return () => window.cancelAnimationFrame(frameId);
  }, [selectedProfileImage, updateContainedImageLayout]);

  useEffect(() => {
    if (
      !completedCrop?.width ||
      !completedCrop?.height ||
      !imageRef.current ||
      !previewCanvasRef.current
    ) {
      return;
    }

    setIsGeneratingCroppedImage(true);
    try {
      renderCropToCanvas(
        imageRef.current,
        previewCanvasRef.current,
        completedCrop,
      );
    } finally {
      setIsGeneratingCroppedImage(false);
    }
  }, [completedCrop]);

  useEffect(() => {
    const previousObjectUrl = objectUrlRef.current;

    if (
      previousObjectUrl &&
      previousObjectUrl !== selectedProfileImage &&
      isObjectUrl(previousObjectUrl)
    ) {
      URL.revokeObjectURL(previousObjectUrl);
    }

    objectUrlRef.current = selectedProfileImage;
  }, [selectedProfileImage]);

  useEffect(() => {
    const stage = imageStageRef.current;

    if (!stage) {
      return;
    }

    const queueLayoutUpdate = () => {
      if (layoutFrameRef.current !== null) {
        window.cancelAnimationFrame(layoutFrameRef.current);
      }

      layoutFrameRef.current = window.requestAnimationFrame(() => {
        layoutFrameRef.current = null;
        updateContainedImageLayout();
      });
    };

    window.addEventListener("resize", queueLayoutUpdate);

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(queueLayoutUpdate)
        : null;

    resizeObserver?.observe(stage);
    queueLayoutUpdate();

    return () => {
      window.removeEventListener("resize", queueLayoutUpdate);
      resizeObserver?.disconnect();

      if (layoutFrameRef.current !== null) {
        window.cancelAnimationFrame(layoutFrameRef.current);
        layoutFrameRef.current = null;
      }
    };
  }, [isOpen, updateContainedImageLayout]);

  useEffect(() => {
    return () => {
      const currentObjectUrl = objectUrlRef.current;
      if (isObjectUrl(currentObjectUrl)) {
        URL.revokeObjectURL(currentObjectUrl);
      }
    };
  }, []);

  const handleReset = (response: any) => {
    if (response?.data?.success) {
      reset();
      dispatch(closeModal());
      dispatch(setSelectedProfileImage(null));
      dispatch(setIsOpenAlertPicModal(false));
    }
  };

  const handleUploadProfilePicture = async () => {
    if (isUploadDisabled) return;

    if (!completedCrop?.width || !completedCrop?.height || !imageRef.current) {
      return;
    }

    const targetUserId = resolveProfileImageUserId(
      selectedId,
      addedUserId,
      employeeJobInfo?.userId,
      loggedInEmployee?.id,
    );
    if (targetUserId === null) return;
    const role = loggedInEmployee?.user_type?.trim().toLowerCase().replace(/[\s_]+/g, "");
    if (Number(targetUserId) !== Number(loggedInEmployee?.id) && !["admin", "hr"].includes(role ?? "")) {
      setValidationError("You can update only your own profile picture.");
      return;
    }

    const file = await getSelectedImageFile(selectedProfileImage);
    if (file.size >= MAX_PROFILE_IMAGE_SIZE) {
      setValidationError(PROFILE_IMAGE_SIZE_VALIDATION_MESSAGE);
      showValidationAlert(PROFILE_IMAGE_SIZE_VALIDATION_MESSAGE);
      return;
    }

    setIsGeneratingCroppedImage(true);
    let croppedFile: File;
    try {
      const uploadCanvas = document.createElement("canvas");
      renderCropToCanvas(imageRef.current, uploadCanvas, completedCrop);
      croppedFile = await canvasToFile(uploadCanvas, "profile.jpg");
    } finally {
      setIsGeneratingCroppedImage(false);
    }

    if (croppedFile.size >= MAX_PROFILE_IMAGE_SIZE) {
      setValidationError(PROFILE_IMAGE_SIZE_VALIDATION_MESSAGE);
      showValidationAlert(PROFILE_IMAGE_SIZE_VALIDATION_MESSAGE);
      return;
    }

    const formData = new FormData();
    formData.append(profile, croppedFile);
    formData.append("user_id", targetUserId.toString());

    const employeeProfilePicRepsonse = await updateEmployeeProfilePic(formData);
    showApiResultAlert(
      employeeProfilePicRepsonse,
      profilePicAlertMeta,
      () => handleReset(employeeProfilePicRepsonse),
      dispatch,
      true,
    );
  };

  return (
    <>
      <ProfileImageModalGlobalStyle />
      <CommonModal
        isOpen={isOpen}
        toggle={handleToggle}
        backdrop={isMandatoryProfileUpload || isLoading ? "static" : true}
        size="lg"
        showFooter={false}
        modalClassName="profile-image-crop-modal"
        modalBodyClassName="p-0"
        contentClassName="profile-image-crop-content overflow-hidden"
        labelledBy="profile-image-crop-title"
      >
        <ModalShell>
          <ModalHeader>
            <HeaderContent>
              <HeaderIcon>
                <IconComponent className="fa fa-user-circle f-18" />
              </HeaderIcon>
              <HeaderText>
                <ModalTitle id="profile-image-crop-title">
                  Upload Profile Picture
                </ModalTitle>
                <ModalDescription>
                  Select the visible profile area, then upload the preview. JPG
                  or PNG, below 2 MB.
                </ModalDescription>
              </HeaderText>
            </HeaderContent>
          </ModalHeader>

          <ImageReviewPanel>
            <ImageCard>
              <ImageCardHeader>
                <ImageCardTitle>
                  <IconComponent className="fa fa-crop" />
                  Crop photo
                </ImageCardTitle>
                <ImageCardHint>Drag corners to adjust</ImageCardHint>
              </ImageCardHeader>
              <ImageStage ref={imageStageRef}>
                <ReactCrop
                  crop={crop}
                  onChange={(_, percentCrop) => {
                    cropRef.current = percentCrop;
                    setCrop(percentCrop);
                  }}
                  onComplete={(pixelCrop) => setCompletedCrop(pixelCrop)}
                  aspect={PROFILE_IMAGE_CROP_ASPECT}
                  keepSelection
                  disabled={isLoading}
                >
                  <SelectedImage
                    ref={imageRef}
                    src={selectedProfileImage}
                    alt={profileImageAlt}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                    style={
                      displayImageSize
                        ? {
                            height: `${displayImageSize.height}px`,
                            visibility: "visible",
                            width: `${displayImageSize.width}px`,
                          }
                        : {
                            height: "1px",
                            visibility: "hidden",
                            width: "1px",
                          }
                    }
                  />
                </ReactCrop>
              </ImageStage>
            </ImageCard>

            <PreviewCard>
              <ImageCardHeader>
                <ImageCardTitle>
                  <IconComponent className="fa fa-eye" />
                  Final {Preview}
                </ImageCardTitle>
                <ImageCardHint>Saved profile view</ImageCardHint>
              </ImageCardHeader>
              <PreviewStage>
                {completedCrop ? (
                  <PreviewCanvas ref={previewCanvasRef} />
                ) : (
                  <PreviewPlaceholder>
                    Select an area to generate preview.
                  </PreviewPlaceholder>
                )}
              </PreviewStage>
            </PreviewCard>
          </ImageReviewPanel>

          {validationError && (
            <ValidationMessage aria-live="polite">
              {validationError}
            </ValidationMessage>
          )}

          <Actions>
            <HiddenFileInput
              ref={fileInputRef}
              type="file"
              accept={PROFILE_IMAGE_ACCEPT}
              aria-label="Choose JPG or PNG profile photo"
              onChange={handleFileChange}
            />
            <Btn
              className="btn-secondary btn-small"
              disabled={isProcessing || isLoading}
              onClick={handleOpenFilePicker}
              type="button"
            >
              Change Photo
            </Btn>
            {!isMandatoryProfileUpload && (
              <Btn
                className="btn-secondary btn-small"
                disabled={isLoading}
                onClick={handleToggle}
                type="button"
              >
                {Cancel}
              </Btn>
            )}
            <Btn
              className="btn-primary btn-small"
              disabled={isUploadDisabled}
              onClick={handleUploadProfilePicture}
              type="button"
            >
              {isLoading ? (
                <>
                  <IconComponent className="fa fa-spinner fa-spin" />{" "}
                  Uploading...
                </>
              ) : isProcessing ? (
                <>
                  <IconComponent className="fa fa-spinner fa-spin" />{" "}
                  Processing...
                </>
              ) : (
                Upload
              )}
            </Btn>
          </Actions>
        </ModalShell>
      </CommonModal>
    </>
  );
};

export default ProfileImageCropModal;
